import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import type { BaseItemSchema, OriginalItemTypes } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { useTranslation } from "react-i18next";
import { Text, TouchableRipple } from "react-native-paper";
import ItemWrapper from "../../ItemWrapper";
import MCQ from "./variants/MCQ";
import NoteCompletion from "./variants/NoteCompletion";

interface Props {
	item: BaseItemSchema;
	originalItemType: OriginalItemTypes;
	selectedChoiceIds: string[] | undefined;
	correctAnswerIds: string[] | undefined;
	crossedOutChoiceIds: string[] | undefined;
	isAnswered?: boolean;
	isSummativeTest?: boolean;
	inReviewMode?: boolean;
	shouldShowCrossout?: boolean;
	onSelectChoice: (choiceId: string, crossingOut?: boolean) => void;
	currentUserResponse?: unknown;
	toggleShowReadMore?: () => void;
	handleHighlightedTextPress?: () => void;
	correctAnswerOrders?: string[];
	updateFormStates: (formStates: Record<string, string>) => void;
}

const ItemRenderer = ({
	item,
	selectedChoiceIds,
	correctAnswerIds,
	crossedOutChoiceIds,
	isAnswered,
	isSummativeTest,
	onSelectChoice,
	toggleShowReadMore,
	shouldShowCrossout,
	originalItemType,
	handleHighlightedTextPress,
	correctAnswerOrders,
	updateFormStates,
	currentUserResponse,
	inReviewMode,
}: Props) => {
	const { isMobile } = useDevice();

	const { content } = item;

	const response = item.response;
	const isMultiSelect =
		response.type === "choice" && response.shuffle
			? response.selectionType === "multiple"
			: false;

	const showHint = !(isSummativeTest || isAnswered);
	const instruction = content?.instruction;
	const instructions =
		(typeof instruction === "string" ? instruction : instruction?.text) ||
		"Select the best answer";

	const showButtonOpenPassage = item.metadata?.type === "INSERT_TEXT";

	const theme = useAppTheme();
	const hasInserted = !!selectedChoiceIds?.length;

	const { t } = useTranslation();

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const ButtonOpenPassage = () => {
		return (
			<TouchableRipple
				onPress={isMobile ? toggleShowReadMore : undefined}
				pointerEvents={isMobile ? "auto" : "none"}
				style={{
					alignItems: "center",
					margin: isMobile ? 16 : 0,
					padding: 12,
					// borderRadius: theme.roundness,
					borderWidth: 1,
					backgroundColor: hasInserted ? theme.colors.primary : "transparent",
					borderColor: theme.colors.primary,
				}}
			>
				<Box
					flexDirection="row"
					width="100%"
					justifyContent="space-between"
					alignItems="center"
				>
					<Text
						style={{
							color: theme.colors[hasInserted ? "white" : "onSurfaceVariant"],
						}}
						variant="labelLarge"
					>
						{hasInserted
							? t("session.passageStem.insertedAtPosition", {
									position:
										(choices?.findIndex((choice) =>
											selectedChoiceIds.includes(choice.id),
										) ?? 0) + 1,
								})
							: t("session.passageStem.notYetInserted")}
					</Text>
					{isMobile && (
						<Text
							variant="labelMedium"
							style={{
								textTransform: "uppercase",
								color: theme.colors[hasInserted ? "white" : "primary"],
							}}
						>
							{t("session.passageStem.openPassage")}
						</Text>
					)}
				</Box>
			</TouchableRipple>
		);
	};

	const choices = response.type === "choice" ? response.choices : [];

	const isSequence = Boolean(
		response.type === "choice" &&
			response.shuffle === false &&
			correctAnswerOrders &&
			correctAnswerOrders.length > 0,
	);

	return (
		<ItemWrapper
			item={item}
			instructions={instructions}
			response={response}
			toggleShowReadMore={toggleShowReadMore}
			originalItemType={originalItemType}
			handleHighlightedTextPress={handleHighlightedTextPress}
			selectedChoiceIds={selectedChoiceIds}
		>
			{showButtonOpenPassage && <ButtonOpenPassage />}

			{(() => {
				switch (response.type) {
					case "choice":
						return showButtonOpenPassage ? null : (
							<MCQ
								response={response}
								multiSelect={isMultiSelect}
								isSummativeTest={isSummativeTest}
								isAnswered={isAnswered}
								inReviewMode={inReviewMode}
								selectedChoiceIds={selectedChoiceIds}
								correctAnswerIds={correctAnswerIds}
								crossedOutChoiceIds={crossedOutChoiceIds}
								choices={choices}
								currentItem={item}
								shouldShowCrossout={shouldShowCrossout}
								handleSelectChoice={onSelectChoice}
								showHint={showHint}
								isMobile={isMobile}
								isSequence={isSequence}
								correctAnswerOrders={correctAnswerOrders}
							/>
						);

					case "form":
						return (
							<NoteCompletion
								itemResponse={response}
								key={item.id}
								countryCode={item.metadata?.countryCode}
								updateFormStates={updateFormStates}
								disabled={isAnswered}
								currentUserResponse={
									currentUserResponse as Record<string, string>
								}
							/>
						);

					case "text":
						return null;

					default:
						return null;
				}
			})()}
		</ItemWrapper>
	);
};

export default ItemRenderer;
